// İSG uzmanı portalı — `Giriş` panelinin `Personel QR` alt-sekmesi: Akış A (brief 092).
// Güvenlik, personelin mobilde ürettiği giriş QR'ını KAMERAYLA okur → `POST /security/entry/verify`
// (`securityApi.verify`). Sonuç KARTINI kendisi BASMAZ (TcLookupTab ile AYNI disiplin, S32b) —
// sonuç `useVerdictStage.ts` üzerinden `VerdictPanel`'e (sayfanın TEK verdict yüzeyi) akar; bu
// bileşen yalnız kamerayı/elle girişi sunar ve sonucu/ağ hatasını yukarı bildirir.
//
// **Kamera kütüphanesi `@zxing/browser` (yalnız `apps/web-expert`, shared'a değil).** İçe aktarım
// DİNAMİK (`await import(...)`) — `QrCodeCanvas.tsx`'in `qrcode` için kullandığı AYNI desen: ağır
// decode motoru ilk bundle'a girmez, yalnız güvenlik kamerayı açınca yüklenir (LCP korunur).
// CSP/CDN yok — paket bundle'a dâhildir, harici ağ çağrısı yapmaz.
//
// **Sürekli tarama + tek-ateşleme koruması.** `PersonnelEntry` jetonu TEK-KULLANIMDIR (§29.2);
// kamera callback'i saniyede onlarca kez ateşler. `busyRef` bir doğrulama SÜRERKEN, `lastTokenRef`
// AYNI kodu (henüz görüş alanındayken) tekrar doğrulamayı engeller — aksi hâlde ikinci okuma
// `TokenConsumed` reddi üretir. Kamera okumalar arasında DURMAZ (güvenlik masası akışı); yalnız
// sekme değişince (bu panel UNMOUNT olur — `TabPanel` varsayılanı) ya da elle kapatınca durur.
//
// **KVKK.** Ekranda/URL'de kalıcı PII yok: decode YERELDİR, sunucuya yalnız opak `qrToken` gider
// (görüntü DEĞİL). Sonuç kartındaki ad yalnız `VerdictPanel`'dedir; bu bileşen TC/ad GÖSTERMEZ.
// Elle giriş alanı yalnız JETONU kabul eder (kimlik verisi değil).
//
// **Yedek yol (kamera izni reddi / cihaz yok).** `getUserMedia` reddinde otomatik olarak elle
// yapıştırma moduna düşer (`denied`/`noDevice`/`error` için ayrı, çözümcül mesaj) — güvenlik yine
// de doğrulama yapabilir. Elle mod her durumda bir düğmeyle de açılabilir.

import {
  Alert,
  Button,
  type EntryVerifyResultDto,
  Field,
  Input,
  Spinner,
  securityApi,
} from '@isgportal/shared';
import { useMutation } from '@tanstack/react-query';
import { Camera, CameraOff, Keyboard, ScanLine } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type ScanMode = 'idle' | 'scanning' | 'manual';
type CameraErrorKind = 'denied' | 'noDevice' | 'error';

export interface ScanTabProps {
  onResult: (result: EntryVerifyResultDto) => void;
  onNetworkError: () => void;
}

export function ScanTab({ onResult, onNetworkError }: ScanTabProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<ScanMode>('idle');
  const [cameraError, setCameraError] = useState<CameraErrorKind | null>(null);
  const [manualToken, setManualToken] = useState('');
  const [manualError, setManualError] = useState<string | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  // Bir doğrulama sürerken yeni okumayı bloke eder; `lastTokenRef` aynı kodun tekrarını.
  const busyRef = useRef(false);
  const lastTokenRef = useRef<string | null>(null);

  const verifyMutation = useMutation({
    mutationFn: (qrToken: string) => securityApi.verify({ qrToken }),
    onSuccess: (result) => {
      onResult(result);
    },
    onError: () => {
      // Ağ/servis hatası — nötr "ağ hatası" kartı (bir ret DEĞİL, TcLookupTab ile aynı kural).
      onNetworkError();
    },
    onSettled: () => {
      busyRef.current = false;
    },
  });

  const verifyToken = useCallback(
    (raw: string): boolean => {
      const token = raw.trim();
      if (!token) {
        return false;
      }
      if (busyRef.current) {
        return false;
      }
      busyRef.current = true;
      lastTokenRef.current = token;
      verifyMutation.mutate(token);
      return true;
    },
    [verifyMutation.mutate],
  );

  // Kamera callback'inin STABİL kalması için ref'te tutulur — decode efekti yalnız `mode`'a
  // bağlıdır, her render'da kamerayı yeniden başlatmaz (aksi hâlde görüntü sürekli zıplardı).
  const verifyTokenRef = useRef(verifyToken);
  useEffect(() => {
    verifyTokenRef.current = verifyToken;
  }, [verifyToken]);

  // Kamera yaşam döngüsü — yalnız `mode==='scanning'` iken; temizlik kamerayı DURDURUR (sekme
  // değişimi/unmount/elle kapatma). `<video>` bu modda render edildiğinden `videoRef` hazırdır.
  useEffect(() => {
    if (mode !== 'scanning') {
      return;
    }
    let cancelled = false;
    let controls: { stop: () => void } | null = null;
    (async () => {
      try {
        const { BrowserQRCodeReader } = await import('@zxing/browser');
        if (cancelled) {
          return;
        }
        const videoEl = videoRef.current;
        if (!videoEl) {
          return;
        }
        const reader = new BrowserQRCodeReader();
        const started = await reader.decodeFromConstraints(
          // Tablet/telefonda arka kamera tercih edilir; desteklenmiyorsa tarayıcı yok sayar.
          { video: { facingMode: 'environment' } },
          videoEl,
          (result) => {
            if (!result) {
              return;
            }
            const text = result.getText();
            if (busyRef.current || text === lastTokenRef.current) {
              return;
            }
            verifyTokenRef.current(text);
          },
        );
        if (cancelled) {
          started.stop();
          return;
        }
        controls = started;
      } catch (err) {
        if (cancelled) {
          return;
        }
        const name = (err as { name?: string } | null)?.name;
        setCameraError(
          name === 'NotAllowedError' || name === 'SecurityError'
            ? 'denied'
            : name === 'NotFoundError' ||
                name === 'OverconstrainedError' ||
                name === 'DevicesNotFoundError'
              ? 'noDevice'
              : 'error',
        );
        // Kamera açılamadı → elle yapıştırma yedeğine düş (güvenlik yine doğrulayabilsin).
        setMode('manual');
      }
    })();
    return () => {
      cancelled = true;
      controls?.stop();
    };
  }, [mode]);

  function openCamera() {
    setCameraError(null);
    setManualError(null);
    lastTokenRef.current = null;
    setMode('scanning');
  }

  function stopCamera() {
    setMode('idle');
  }

  function openManual() {
    setManualError(null);
    setMode('manual');
  }

  function submitManual() {
    if (verifyMutation.isPending) {
      return;
    }
    if (!manualToken.trim()) {
      setManualError(t('security.scan.manual.validation.required'));
      return;
    }
    setManualError(null);
    if (verifyToken(manualToken)) {
      setManualToken('');
    }
  }

  // ---- idle: kamerayı açmaya davet ----
  if (mode === 'idle') {
    return (
      <div className="flex min-h-[16rem] flex-col items-center justify-center gap-4 p-8 text-center">
        <ScanLine className="h-12 w-12 text-primary" aria-hidden="true" />
        <div className="flex flex-col gap-1.5">
          <h3 className="text-lg font-semibold text-fg">{t('security.scan.intro.title')}</h3>
          <p className="max-w-sm text-sm text-muted">{t('security.scan.intro.body')}</p>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <Button size="lg" onClick={openCamera}>
            <Camera className="h-5 w-5 shrink-0" aria-hidden="true" />
            {t('security.scan.start')}
          </Button>
          <Button size="lg" variant="secondary" onClick={openManual}>
            <Keyboard className="h-5 w-5 shrink-0" aria-hidden="true" />
            {t('security.scan.manual.toggle')}
          </Button>
        </div>
      </div>
    );
  }

  // ---- manual: elle token yapıştırma (kamera reddi/cihaz yok ya da tercih) ----
  if (mode === 'manual') {
    return (
      <div className="flex flex-col gap-4 p-5">
        {cameraError ? (
          <Alert tone={cameraError === 'denied' ? 'warning' : 'danger'}>
            <p className="font-medium">{t(`security.scan.cameraError.${cameraError}.title`)}</p>
            <p className="text-sm">{t(`security.scan.cameraError.${cameraError}.body`)}</p>
          </Alert>
        ) : (
          <p className="text-sm text-muted">{t('security.scan.manual.hint')}</p>
        )}
        <form
          className="flex items-end gap-3"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            submitManual();
          }}
        >
          <Field
            htmlFor="security-scan-manual"
            label={t('security.scan.manual.label')}
            error={manualError ?? undefined}
            className="flex-1"
          >
            <Input
              id="security-scan-manual"
              autoComplete="off"
              placeholder={t('security.scan.manual.placeholder')}
              invalid={Boolean(manualError)}
              value={manualToken}
              onChange={(e) => setManualToken(e.target.value)}
            />
          </Field>
          <Button type="submit" size="lg" loading={verifyMutation.isPending}>
            {t('security.scan.manual.submit')}
          </Button>
        </form>
        <div>
          <Button variant="ghost" size="sm" onClick={openCamera}>
            <Camera className="h-4 w-4 shrink-0" aria-hidden="true" />
            {t('security.scan.manual.tryCamera')}
          </Button>
        </div>
      </div>
    );
  }

  // ---- scanning: canlı kamera önizlemesi + sürekli decode ----
  return (
    <div className="flex flex-col gap-4 p-5">
      <div className="relative mx-auto w-full max-w-sm overflow-hidden rounded-md border border-border bg-black">
        {/* playsInline: iOS'ta tam ekrana geçmeden satır içinde oynat. muted + autoPlay:
            getUserMedia akışı kullanıcı etkileşimi olmadan oynayabilsin. Canlı kamera akışı
            (decode önizlemesi) — altyazı kaynağı yok; aria-label ile adlandırılır. */}
        <video
          ref={videoRef}
          className="aspect-square w-full object-cover"
          aria-label={t('security.scan.cameraAriaLabel')}
          muted
          autoPlay
          playsInline
        />
        {/* Hedef çerçevesi — QR'ı ortaya hizalamaya yardım eder (dekoratif). */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
        >
          <div className="h-40 w-40 rounded-lg border-2 border-white/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.25)]" />
        </div>
        {verifyMutation.isPending ? (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <Spinner label={t('security.scan.verifying')} className="text-white" />
          </div>
        ) : null}
      </div>

      <p className="text-center text-sm text-muted" aria-live="polite">
        {verifyMutation.isPending ? t('security.scan.verifying') : t('security.scan.instruction')}
      </p>

      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button variant="secondary" onClick={stopCamera}>
          <CameraOff className="h-4 w-4 shrink-0" aria-hidden="true" />
          {t('security.scan.stop')}
        </Button>
        <Button variant="ghost" onClick={openManual}>
          <Keyboard className="h-4 w-4 shrink-0" aria-hidden="true" />
          {t('security.scan.manual.toggle')}
        </Button>
      </div>
    </div>
  );
}
